import React, { useState } from 'react';
import { Activity, Play, Settings } from 'lucide-react';
import { authenticatedFetchJSON } from '../../utils/authenticatedFetch';

type BearingAnalysisType = 'life_l10' | 'equivalent_load' | 'clearance' | 'fit_pressure' | 'frequencies' | 'grease_quantity' | 'spring_rate' | 'recommend';

interface AnalysisResult {
    [key: string]: any;
}

const BearingAnalysis: React.FC = () => {
    const [analysisType, setAnalysisType] = useState<BearingAnalysisType>('life_l10');
    const [params, setParams] = useState<{ [key: string]: string | number }>({});
    const [result, setResult] = useState<AnalysisResult | null>(null);
    const [loading, setLoading] = useState(false);

    const analysisOptions: { value: BearingAnalysisType, label: string }[] = [
        { value: 'life_l10', label: 'L10 Fatigue Life (ISO 281)' },
        { value: 'equivalent_load', label: 'Equivalent Dynamic Load' },
        { value: 'clearance', label: 'Operating Clearance' },
        { value: 'fit_pressure', label: 'Fit Pressure & Force' },
        { value: 'frequencies', label: 'Vibration Frequencies' },
        { value: 'grease_quantity', label: 'Grease Quantity' },
        { value: 'spring_rate', label: 'Stiffness / Spring Rate' },
        { value: 'recommend', label: 'Bearing Recommendation (AI)' },
    ];

    const getFields = (type: BearingAnalysisType) => {
        switch (type) {
            case 'life_l10': return [
                { key: 'dynamic_capacity_c', label: 'Dynamic Capacity C (N)', type: 'number' },
                { key: 'equivalent_load_p', label: 'Equivalent Load P (N)', type: 'number' },
                { key: 'speed_rpm', label: 'Speed (RPM)', type: 'number' },
                { key: 'bearing_type', label: 'Type (ball/roller)', type: 'select', options: ['ball', 'roller'] }
            ];
            case 'equivalent_load': return [
                { key: 'radial_load_fr', label: 'Radial Load Fr (N)', type: 'number' },
                { key: 'axial_load_fa', label: 'Axial Load Fa (N)', type: 'number' },
                { key: 'x_factor', label: 'X Factor', type: 'number' },
                { key: 'y_factor', label: 'Y Factor', type: 'number' }
            ];
            case 'clearance': return [
                { key: 'initial_clearance_um', label: 'Initial Clearance (µm)', type: 'number' },
                { key: 'inner_fit_interference_um', label: 'Interference Fit (µm)', type: 'number' },
                { key: 'temp_differential_c', label: 'Temp Difference (°C)', type: 'number' },
                { key: 'bearing_bore_mm', label: 'Bore Diameter (mm)', type: 'number' }
            ];
            case 'frequencies': return [
                { key: 'speed_rpm', label: 'Speed (RPM)', type: 'number' },
                { key: 'pitch_diameter_mm', label: 'Pitch Diameter (mm)', type: 'number' },
                { key: 'ball_diameter_mm', label: 'Ball Diameter (mm)', type: 'number' },
                { key: 'num_balls', label: 'Number of Balls', type: 'number' }
            ];
            // Add others as needed
            default: return [];
        }
    };

    const handleCalculate = async () => {
        setLoading(true);
        setResult(null);
        try {
            const payload = {
                analysis_type: analysisType,
                parameters: Object.entries(params).reduce((acc, [k, v]) => ({
                    ...acc,
                    [k]: typeof v === 'string' && !isNaN(parseFloat(v)) ? parseFloat(v) : v
                }), {})
            };

            const data = await authenticatedFetchJSON('/api/v1/lab/bearing-analysis', {
                method: 'POST',
                body: JSON.stringify(payload),
            });
            setResult(data);
        } catch (error) {
            console.error(error);
            setResult({ error: "Calculation failed" });
        } finally {
            setLoading(false);
        }
    };

    const currentFields = getFields(analysisType);

    return (
        <div className="space-y-6">
            <div className="bg-card border border-border p-6 rounded-lg shadow-sm">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                        <Settings className="text-primary" /> Bearing Analysis
                    </h2>
                    <select
                        value={analysisType}
                        onChange={(e) => {
                            setAnalysisType(e.target.value as BearingAnalysisType);
                            setParams({});
                            setResult(null);
                        }}
                        className="bg-background border border-border rounded px-3 py-1.5 text-sm outline-none focus:border-primary"
                    >
                        {analysisOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                        {currentFields.length > 0 ? (
                            currentFields.map(field => (
                                <div key={field.key}>
                                    <label className="block text-sm font-medium mb-1 text-text-secondary">{field.label}</label>
                                    {field.type === 'select' ? (
                                        <select
                                            className="w-full bg-background border border-border rounded p-2 text-sm focus:ring-1 focus:ring-primary"
                                            value={params[field.key] || ''}
                                            onChange={(e) => setParams(prev => ({ ...prev, [field.key]: e.target.value }))}
                                        >
                                            <option value="">Select...</option>
                                            {field.options?.map(o => <option key={o} value={o}>{o}</option>)}
                                        </select>
                                    ) : (
                                        <input
                                            type="number"
                                            className="w-full bg-background border border-border rounded p-2 text-sm focus:ring-1 focus:ring-primary"
                                            value={params[field.key] || ''}
                                            onChange={(e) => setParams(prev => ({ ...prev, [field.key]: e.target.value }))}
                                        />
                                    )}
                                </div>
                            ))
                        ) : (
                            <div className="text-text-secondary italic">Select an analysis type to view parameters.</div>
                        )}

                        <button
                            onClick={handleCalculate}
                            disabled={loading}
                            className="w-full mt-4 bg-primary text-white py-2 rounded flex items-center justify-center gap-2 hover:bg-primary/90 disabled:opacity-50"
                        >
                            {loading ? <Activity className="animate-spin" /> : <Play size={16} />}
                            Calculate
                        </button>
                    </div>

                    <div className="bg-muted/20 border border-border rounded p-4 h-full min-h-[200px]">
                        <h3 className="text-sm font-semibold text-text-secondary mb-2 uppercase tracking-wide">Results</h3>
                        {result ? (
                            <div className="space-y-2">
                                {Object.entries(result).map(([key, value]) => (
                                    <div key={key} className="flex justify-between items-start border-b border-border/50 last:border-0 pb-2">
                                        <span className="text-sm text-text-secondary font-medium">{key.replace(/_/g, ' ')}:</span>
                                        <span className="text-sm font-mono text-text-primary text-right">{typeof value === 'number' ? value.toFixed(4) : String(value)}</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="h-full flex items-center justify-center text-text-secondary/50 text-sm">
                                No results calculated
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BearingAnalysis;
